import { type PaletteKey, PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Tidecaller's shots (the Abyssal Angler set's gun, ADR-0160 amended): a harpoon dart glowing with the deep's own light
 * (white at its heart, sea-green and yellow, never the team's blue) trailing a stream of bubbles; a ring of water
 * punched out of the muzzle with bubbles; a splash crown where it lands. Distinct from the Garden hose's water (a
 * blue drop, a spray, a crown of round drops) and the Bubble gun's soap: a barbed dart, rings of bubbles, a crown of spikes. And the little anglerfish its knock-out
 * flourish throws out of a geyser.
 */

/**
 * A bubble round the pixel corner (cx, cy): r 1 a 4-px ring, r 2 a 6-px one, r 0 a 2-px speck; lit white on its
 * top-left, pale grey on its far side.
 */
const bubble = (x: number, y: number, cx: number, cy: number, r: number): Tone => {
  const [dx, dy] = [x - cx, y - cy];
  const d = Math.hypot(dx, dy);
  if (r === 0) return d < 0.8 ? 'w' : null;
  if (Math.abs(d - (r === 1 ? 1.58 : 2.5)) >= 0.45) return null;
  return dx + dy < 0.3 ? 'w' : '5';
};

/** The first of `bubbles` ([x, y, r]) (x, y) is on, or null. */
const onBubbles = (x: number, y: number, bubbles: readonly (readonly [number, number, number])[]): Tone => {
  for (const [cx, cy, r] of bubbles) {
    const t = bubble(x, y, cx, cy, r);
    if (t) return t;
  }
  return null;
};

// --- The dart --------------------------------------------------------------------------------------------------------

/** The bubbles streaming off the dart's tail, frame by frame (each drifting back as it flies on). */
const STREAM = [
  [
    [12, 4, 1],
    [6, 12, 1],
    [2, 6, 0],
  ],
  [
    [10, 12, 1],
    [4, 4, 1],
    [14, 5, 0],
  ],
  [
    [8, 4, 1],
    [3, 11, 0],
    [14, 12, 0],
  ],
] as const;

/** The dart: a spearhead glowing white at its heart, its barbs swept back, a shaft of light behind, bubbles off it. */
const dart = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    // The head: from its barbs (x 17) to its point (x 29).
    if (x >= 17 && x < 29.5) {
      const t = (29.5 - x) / 12;
      const half = 3.6 * Math.min(1, t * 1.25);
      const d = Math.abs(y - 8) / half;
      if (d < 1) return d < 0.42 || x > 27 ? 'w' : y < 8 ? 'y' : 'l';
    }
    // Its barbs.
    for (const s of [-1, 1]) if (x > 14 && x < 18 && Math.abs(y - 8 - s * (3.4 + (18 - x) * 0.45)) < 0.75) return 'l';
    // The shaft of light, flickering at its end.
    if (x >= 6 + f && x < 17 && y > 7 && y < 9.2) return y < 8 ? 'y' : 'l';
    return onBubbles(x, y, STREAM[f] ?? STREAM[0]);
  });

// --- The muzzle ------------------------------------------------------------------------------------------------------

/** A ring of water punched out of the muzzle per frame: [x, rx, ry], whole on the first two, broken on the last. */
const RINGS = [
  [12, 2.2, 7],
  [21, 3, 10],
  [30, 3.4, 12],
] as const;
const MUZZLE_BUBBLES = [
  [
    [16, 12, 1],
    [18, 29, 1],
    [21, 17, 0],
  ],
  [
    [25, 8, 2],
    [28, 32, 1],
    [16, 15, 1],
    [32, 24, 0],
    [30, 14, 0],
  ],
  [
    [33, 6, 2],
    [37, 34, 2],
    [25, 27, 1],
    [41, 19, 1],
    [22, 11, 0],
  ],
] as const;

const surge = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      // The flash at the muzzle: white, the deep's green light round it.
      const d = Math.hypot(x - 6, (y - 20) * 1.15);
      if (d < 2.6) return 'w';
      if (d < 4.6) return 'y';
      if (d < 5.8) return 'l';
      // Spray fanning out of it.
      for (const a of [-0.55, -0.2, 0.2, 0.55]) {
        const along = (x - 6) * Math.cos(a) + (y - 20) * Math.sin(a);
        const off = -(x - 6) * Math.sin(a) + (y - 20) * Math.cos(a);
        if (along > 6 && along < 16 && Math.abs(off) < 0.7) return along < 11 ? 'w' : '5';
      }
    }
    const [rx0, rx, ry] = RINGS[f] ?? RINGS[0];
    const e = Math.hypot((x - rx0) / rx, (y - 20) / ry);
    if (Math.abs(e - 1) < 0.22 && (f < 2 || Math.sin(Math.atan2(y - 20, x - rx0) * 5) > -0.2))
      return x > rx0 ? (f === 2 ? '5' : 'w') : f === 2 ? '4' : '5';
    return onBubbles(x, y, MUZZLE_BUBBLES[f] ?? MUZZLE_BUBBLES[0]);
  });

// --- The splash ------------------------------------------------------------------------------------------------------

/** The splash per frame: its ripple on the water (rx, ry), how high its crown stands, the bubbles rising off it. */
const SPLASH = [
  { ripple: [0, 0], crown: 0, bubbles: [] },
  {
    ripple: [11, 4.5],
    crown: 9,
    bubbles: [
      [16, 12, 1],
      [29, 10, 1],
    ],
  },
  {
    ripple: [15, 6],
    crown: 6,
    bubbles: [
      [14, 7, 2],
      [30, 5, 1],
      [23, 10, 0],
    ],
  },
  {
    ripple: [18.5, 7.5],
    crown: 0,
    bubbles: [
      [13, 3, 1],
      [31, 2, 2],
      [22, 6, 0],
    ],
  },
] as const;

const splash = (f: number) => (c: PixelCanvas) => {
  const { ripple, crown, bubbles } = SPLASH[f] ?? SPLASH[0];
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    if (f === 0) {
      // It strikes: a white heart in the deep's green light, spray thrown out in eight spikes.
      const a = Math.atan2(dy, dx);
      const d = Math.hypot(dx, dy);
      const reach = 6 + 6 * Math.abs(Math.cos(a * 4)) ** 3;
      if (d < 3.2) return 'w';
      if (d < 5) return 'y';
      if (d < 6.2) return 'l';
      return d < reach ? (d < reach - 2 ? '5' : 'w') : null;
    }
    const b = onBubbles(x, y, bubbles);
    if (b) return b;
    // The ripple spreading on the water, broken as it fades.
    const [rx, ry] = ripple;
    const e = Math.hypot(dx / rx, (y - 26) / ry);
    if (Math.abs(e - 1) < 0.1 + 0.6 / rx && (f < 3 || Math.sin(Math.atan2(y - 26, dx) * 6) > 0))
      return y > 26 ? (f === 3 ? '4' : '5') : f === 3 ? '5' : 'w';
    // The crown: spikes of water standing up round its back half, a drop leaping off each.
    if (crown)
      for (let k = 0; k < 7; k++) {
        const a = Math.PI * (1.08 + (k / 6) * 0.84);
        const [bx, by] = [22 + Math.cos(a) * rx * 0.8, 26 + Math.sin(a) * ry * 0.8];
        const lean = Math.cos(a) * 0.35;
        const up = by - y;
        if (up >= 0 && up < crown) {
          const half = 1.3 * (1 - up / crown);
          if (Math.abs(x - bx - lean * up) < half + 0.2) return up > crown * 0.55 ? 'w' : '5';
        }
        if (Math.hypot(x - bx - lean * (crown + 2), y - (by - crown - 2)) < 0.9) return f === 1 ? 'w' : 'l';
      }
    return null;
  });
};

// --- The little anglerfish -------------------------------------------------------------------------------------------

/** Its tail per frame (flop: up, level, down, level) and how its front bends with it. */
const FLOP = [-0.55, 0, 0.55, 0] as const;

/**
 * A little anglerfish flopping on the ground (20×16, its middle the origin): a slate body lit on its back, a pale belly,
 * a gaping mouth of needle teeth, a white eye, its own lure glowing on a stalk, its tail flicking. A 1-px navy rim so it
 * reads on lawn and lane.
 */
const fish = (f: number) => (c: PixelCanvas) => {
  const tilt = FLOP[f] ?? 0;
  const bend = -tilt * 1.2;
  const body: PixelCanvas = new PixelCanvas(c.w, c.h);
  paint(body, (x, y) => {
    const cy = 8.5 + bend * ((x - 10) / 6);
    // The tail: a fan behind the body, flicking.
    const u = 7 - x;
    if (u > 0 && u < 5.5) {
      const v = y - (8.5 - tilt * u * 1.1);
      if (Math.abs(v) < 0.75 * u + 0.4) return Math.abs(v) < 0.3 * u ? '3' : '2';
    }
    const e = Math.hypot((x - 11) / 5.6, (y - cy) / 4.4);
    if (e >= 1) return null;
    // The mouth: a dark gape at its front, needle teeth.
    if (x > 13.5 && y > cy + 0.2 && y < cy + 2.4) return Math.floor(x) % 2 === 0 && y < cy + 1.2 ? 'w' : 'k';
    // The eye.
    if (Math.hypot(x - 13.5, y - (cy - 1.5)) < 1) return x > 13.5 ? 'k' : 'w';
    return y < cy - 2.6 ? '4' : y < cy + 1 ? '3' : y < cy + 2.6 ? '4' : '5';
  });
  // A 1-px navy rim round it.
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) {
      if (body.get(x, y) !== null) continue;
      if ([body.get(x - 1, y), body.get(x + 1, y), body.get(x, y - 1), body.get(x, y + 1)].some((p) => p !== null))
        c.set(x, y, '1');
    }
  c.blit(body);
  // Its lure: a stalk off its brow arching forward, a bulb of green light at its end.
  const top = Math.round(4.2 + bend * 0.5);
  const stalk: readonly (readonly [number, number, PaletteKey])[] = [
    [12, top, '2'],
    [12, top - 1, '2'],
    [13, top - 2, '2'],
    [14, top - 3, '2'],
    [15, top - 3, '2'],
    [16, top - 2, '2'],
  ];
  for (const [x, y, k] of stalk) c.set(x, y, k);
  c.set(16, top - 1, f % 2 ? 'y' : 'w')
    .set(17, top - 1, 'l')
    .set(16, top, 'l')
    .set(17, top, 'l');
};

export default [
  sprite({
    name: 'tidecaller_shot',
    ...shot,
    notes: `Tidecaller's bullet (ADR-0160 amended): a harpoon dart of the deep's own light, a spearhead (the stock pellet's
size) white at its heart, yellow over sea-green, its barbs swept back, a shaft of light behind it, a stream of bubbles
(rings, lit top-left) drifting off its tail. Never the team's blue. Head right. Rubric (4×): S5 R4 P5 L4 C4 A4 F4.
Weakest: its sea-green on the lawn; the white heart, its glow and its tracer carry it there.`,
    anims: { idle: { fps: 12, frames: frames3(dart) } },
  }),
  sprite({
    name: 'tidecaller_muzzle',
    ...muzzle,
    notes: `Tidecaller's muzzle flash (ADR-0160 amended): a white flash in green light at the muzzle, spray fanning out, a
ring of water punched out and flying on (whole, then wider, then breaking), bubbles thrown with it. Points right.
Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the ring is 2 px, a white oval at 1:1.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(surge) } },
  }),
  sprite({
    name: 'tidecaller_burst',
    ...burst,
    notes: `Tidecaller's hit (ADR-0160 amended): it strikes in a white heart and green light, spray thrown in eight spikes;
then a splash crown standing up round the back of a ripple, a drop leaping off each spike, bubbles rising; the ripple
spreading and breaking. 4 frames. Rubric (4×): S5 R4 P5 L4 C4 A4 F4. Weakest: the ripple is thick at its front.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(splash) } },
  }),
  sprite({
    name: 'angler_bit',
    group: 'fx',
    size: [20, 16],
    origin: [10, 8],
    density: 4,
    notes: `The Abyssal Angler's knock-out (ADR-0160 amended): a little anglerfish thrown out of the geyser, flopping on the
ground (tail up, level, down, level): a slate body, a pale belly, a gape of needle teeth, its own green lure. A 1-px
navy rim. Rubric (4×): S4 R4 P4 L4 C4 A4 F4. Weakest: its lure's stalk is lost at 1:1 (a green dot over it).`,
    anims: { idle: { fps: 10, frames: frames4(fish) } },
  }),
];
